import { cloneElement, isValidElement } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { BOX_SHADOW_BUTTON } from "../../utils/colors";

const OPTION_ICON_SX = {
  fontSize: "20px !important",
  width: 20,
  height: 20,
  flexShrink: 0,
  display: "block",
};

const OptionIcon = ({ icon }) => {
  if (!icon) return null;

  if (isValidElement(icon)) {
    return cloneElement(icon, {
      sx: {
        ...OPTION_ICON_SX,
        ...(icon.props?.sx || {}),
      },
    });
  }

  const IconComponent = icon;
  return <IconComponent sx={OPTION_ICON_SX} />;
};

const SegmentedControl = ({
  options = [],
  selectedValue = "",
  onChange,
  name = "segmented_control",
  readOnly = false,
}) => {
  const selected = typeof selectedValue === "string" ? selectedValue : "";
  const interactive = !readOnly;

  const onSelect = (value) => {
    if (!interactive || typeof onChange !== "function") return;
    onChange(value);
  };

  return (
    <Box
      role="radiogroup"
      aria-label={name}
      sx={{
        width: "100%",
        bgcolor: "#f5f5f5",
        borderRadius: 2,
        p: 0.8,
        display: "grid",
        gridTemplateColumns: `repeat(${Math.max(options.length, 1)}, 1fr)`,
        gap: 0.5,
      }}
    >
      {options.map((option) => {
        const inputId = `${name}-${option.value}`;
        const isSelected = selected === option.value;

        return (
          <Box
            key={option.value}
            component="label"
            htmlFor={inputId}
            sx={{
              minHeight: 40,
              height: 40,
              px: 1,
              borderRadius: 1.5,
              fontSize: 14,
              fontWeight: 500,
              transition: "all 0.2s",
              cursor: interactive ? "pointer" : "default",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              textAlign: "center",
              position: "relative",
              overflow: "visible",
              ...(isSelected && {
                bgcolor: "background.paper",
                color: "text.primary",
                boxShadow: BOX_SHADOW_BUTTON,
              }),
              ...(!isSelected && {
                color: "text.secondary",
              }),
              opacity: interactive ? 1 : 0.92,
            }}
          >
            <Box
              component="input"
              type="radio"
              id={inputId}
              name={name}
              value={option.value}
              checked={isSelected}
              disabled={!interactive}
              onChange={() => onSelect(option.value)}
              sx={{
                position: "absolute",
                opacity: 0,
                width: 0,
                height: 0,
                pointerEvents: "none",
              }}
            />
            <Typography
              component="span"
              sx={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 0.75,
                fontSize: 14,
                fontWeight: 500,
                lineHeight: 1,
                color: "inherit",
                whiteSpace: "nowrap",
              }}
            >
              <OptionIcon icon={option.icon} />
              {option.label}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
};

export default SegmentedControl;
